'use client';

import { apiClient } from '@/lib/api-client';
import {
  CommonResponse,
  PetrohqApiResponseCommonResponse,
} from '@/lib/api/data-contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from './auth-context';

/** GET /common 의 유종 - 주유소별 행(pos.store_oil). 같은 유종코드가 주유소마다 다른 상품일 수 있다 */
export interface StoreOil {
  storeCd?: string;
  oilCode?: string;
  oilName?: string;
  oilKind?: string;
}

interface CommonContextType {
  commonData: CommonResponse | null;
  isLoading: boolean;
  error: string | null;
  refreshCommonData: () => Promise<void>;
  // 편의 함수들
  getDealTypes: () => Array<{ typeCode?: string; typeName?: string }>;
  getCommonCodes: () => Array<any>;
  /** storeCd 를 주면 그 주유소 유종만, 없으면 코드별 첫 행(조회조건 목록용) */
  getOils: (storeCd?: string) => StoreOil[];
}

const CommonContext = createContext<CommonContextType | undefined>(undefined);

export function useCommon() {
  const context = useContext(CommonContext);
  if (context === undefined) {
    throw new Error('useCommon must be used within a CommonProvider');
  }
  return context;
}

interface CommonProviderProps {
  children: React.ReactNode;
}

export function CommonProvider({ children }: CommonProviderProps) {
  const { isAuthenticated } = useAuth();
  const [commonData, setCommonData] = useState<CommonResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCommonData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('common', {
        headers: {
          'user-agent': navigator.userAgent,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PetrohqApiResponseCommonResponse = await response.json();

      if (data.status === 200 && data.result) {
        setCommonData(data.result);
      } else {
        throw new Error(data.message || 'Failed to fetch common data');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching common data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshCommonData = useCallback(async () => {
    if (!isAuthenticated) return;
    await fetchCommonData();
  }, [fetchCommonData, isAuthenticated]);

  // 편의 함수들
  const getDealTypes = useCallback(() => {
    return commonData?.dealtype || [];
  }, [commonData]);

  const getCommonCodes = useCallback(() => {
    return commonData?.commonCode || [];
  }, [commonData]);

  const getOils = useCallback(
    (storeCd?: string) => {
      const oils = (commonData?.oil || []) as StoreOil[];
      if (storeCd) return oils.filter(oil => oil.storeCd === storeCd);
      const seen = new Set<string | undefined>();
      return oils.filter(oil => !seen.has(oil.oilCode) && !!seen.add(oil.oilCode));
    },
    [commonData]
  );

  // 인증 완료 후에만 데이터 로드 (토큰 없이 호출 시 401 방지)
  useEffect(() => {
    if (isAuthenticated) {
      fetchCommonData();
    }
  }, [fetchCommonData, isAuthenticated]);

  const value: CommonContextType = {
    commonData,
    isLoading,
    error,
    refreshCommonData,
    getDealTypes,
    getCommonCodes,
    getOils,
  };

  return (
    <CommonContext.Provider value={value}>{children}</CommonContext.Provider>
  );
}
